import React, { Component } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import api from "../utils/api";
import { toast } from "react-toastify";

const formatDateOnly = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatBucketLabel = (bucket, reportType) => {
  const d = new Date(bucket);
  if (reportType === "monthly") {
    return d.toLocaleString("default", { month: "short", year: "numeric" });
  }
  if (reportType === "weekly") {
    const end = new Date(d);
    end.setDate(d.getDate() + 6);
    const fmt = (x) => x.toLocaleString("default", { month: "short", day: "numeric" });
    return `${fmt(d)} - ${fmt(end)}`;
  }
  return d.toLocaleString("default", { month: "short", day: "numeric" });
};

class AdminBudgetSummaryReportPage extends Component {
  state = {
    reportType: "daily",
    startDate: "",
    endDate: "",

    serviceType: "",
    medicineType: [],
    serviceFor: [],
    location: [],

    medicineTypeOptions: [],
    serviceForOptions: [],
    serviceTypeOptions: [],

    buckets: [],
    platforms: [],
    sources: [],
    rows: [],

    expandedPlatformId: null,
    loading: false,
    dataFetched: false,
  };

  componentDidMount() {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const d = formatDateOnly(yesterday);
    this.setState({ startDate: d, endDate: d }, () => {
      this.loadFilterOptions();
      this.fetchData();
    });
  }

  loadFilterOptions = async () => {
    try {
      const res = await api.get("/const-data");
      const constData = res.data.data || res.data || {};
      this.setState({
        medicineTypeOptions: constData.medicine_type || [],
        serviceForOptions: constData.service_for || [],
        serviceTypeOptions: constData.enquiry_type || [],
      });
    } catch (err) {
      console.error("Failed to load filter options", err);
    }
  };

  loadLocations = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) return [];
    try {
      const res = await api.get("/cities", { params: { q: inputValue } });
      const cities = res.data.data || res.data || [];
      return cities.map((city) => ({
        value: `${city.city}, ${city.state}`,
        label: `${city.city}, ${city.state}`,
        id: city.id,
      }));
    } catch (err) {
      return [];
    }
  };

  validateDateRange = () => {
    const { startDate, endDate } = this.state;
    if (!startDate || !endDate) return "Please select both start and end dates.";
    const s = new Date(startDate);
    const e = new Date(endDate);
    if (e < s) return "End date must be after start date.";
    const diffDays = (e - s) / (1000 * 60 * 60 * 24);
    if (diffDays > 186) return "Date range cannot exceed 6 months.";
    return null;
  };

  fetchData = async () => {
    const err = this.validateDateRange();
    if (err) { toast.error(err); return; }

    this.setState({ loading: true });
    try {
      const { reportType, startDate, endDate, serviceType, medicineType, serviceFor, location } = this.state;
      const params = { report_type: reportType, start_date: startDate, end_date: endDate };
      if (serviceType) params.service_type = serviceType;
      if (medicineType.length > 0) params.medicine_type = medicineType.map((m) => m.value).join(",");
      if (serviceFor.length > 0) params.service_for = serviceFor.map((s) => s.value).join(",");
      if (location.length > 0) params.location = JSON.stringify(location.map((l) => l.value));

      const res = await api.get("/admin/marketing/budget-summary", { params });
      if (res.data.success) {
        const { buckets, platforms, sources, rows } = res.data.data;
        this.setState({ buckets, platforms, sources, rows, dataFetched: true, expandedPlatformId: null });
      }
    } catch (err2) {
      toast.error(err2.response?.data?.message || "Error fetching budget summary");
    } finally {
      this.setState({ loading: false });
    }
  };

  togglePlatform = (platformId) => {
    this.setState((prev) => ({ expandedPlatformId: prev.expandedPlatformId === platformId ? null : platformId }));
  };

  // source_id -> { [bucket]: {budget, spent, total_leads, posted_leads} }, for one platform
  sourceBucketMap = (platformId) => {
    const { rows } = this.state;
    const map = {};
    rows.filter((r) => r.platform_id === platformId).forEach((r) => {
      if (!map[r.source_id]) map[r.source_id] = {};
      map[r.source_id][r.bucket] = r;
    });
    return map;
  };

  platformTotals = (platformId) => {
    const { rows } = this.state;
    return rows.filter((r) => r.platform_id === platformId).reduce(
      (acc, r) => {
        acc.budget += Number(r.budget || 0);
        acc.spent += Number(r.spent || 0);
        acc.total_leads += Number(r.total_leads || 0);
        acc.posted_leads += Number(r.posted_leads || 0);
        return acc;
      },
      { budget: 0, spent: 0, total_leads: 0, posted_leads: 0 }
    );
  };

  render() {
    const {
      reportType, startDate, endDate,
      serviceType, medicineType, serviceFor, location,
      medicineTypeOptions, serviceForOptions, serviceTypeOptions,
      buckets, platforms, sources, loading, dataFetched, expandedPlatformId,
    } = this.state;

    const sourceById = {};
    sources.forEach((s) => { sourceById[s.id] = s.title; });

    const grandTotal = platforms.reduce((acc, p) => {
      const t = this.platformTotals(p.id);
      acc.budget += t.budget;
      acc.spent += t.spent;
      acc.total_leads += t.total_leads;
      acc.posted_leads += t.posted_leads;
      return acc;
    }, { budget: 0, spent: 0, total_leads: 0, posted_leads: 0 });

    const selectStyles = {
      control: (base) => ({ ...base, minHeight: "38px", borderRadius: "9px", borderColor: "var(--adm-border)", background: "#f8fafc" }),
    };

    return (
      <div className="adm-page">
        <div className="adm-page-inner">
          <div className="adm-header">
            <div>
              <h4 className="adm-header-title">
                <span className="adm-icon-badge"><i className="fas fa-layer-group"></i></span>
                Budget Summary
              </h4>
              <p className="adm-header-sub">
                Platform-wise sources, budget and spent, bucketed by day/week/month. Leads are narrowed by the filters below; budget and spent are not.
              </p>
            </div>
            {dataFetched && platforms.length > 0 && (
              <div className="adm-toolbar">
                <span className="adm-badge adm-badge-primary"><i className="fas fa-wallet"></i>₹ {grandTotal.budget.toFixed(0)}</span>
                <span className="adm-badge adm-badge-muted"><i className="fas fa-receipt"></i>₹ {grandTotal.spent.toFixed(0)}</span>
                <span className="adm-badge adm-badge-muted"><i className="fas fa-users"></i>{grandTotal.total_leads} leads</span>
              </div>
            )}
          </div>

          <div className="adm-card">
            <div className="adm-card-body">
              <div className="adm-filter-bar" style={{ marginBottom: "16px" }}>
                <div className="adm-filter-group">
                  <label>Report Type</label>
                  <select value={reportType} onChange={(e) => this.setState({ reportType: e.target.value })}>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly (Mon-Sun)</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div className="adm-filter-group">
                  <label>From</label>
                  <input type="date" value={startDate} onChange={(e) => this.setState({ startDate: e.target.value })} />
                </div>
                <div className="adm-filter-group">
                  <label>To</label>
                  <input type="date" value={endDate} onChange={(e) => this.setState({ endDate: e.target.value })} />
                </div>
                <div className="adm-filter-group">
                  <label>Service Type</label>
                  <select value={serviceType} onChange={(e) => this.setState({ serviceType: e.target.value })}>
                    <option value="">All</option>
                    {serviceTypeOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="adm-filter-bar">
                <div className="adm-filter-group grow">
                  <label>Medicine Type</label>
                  <div className="adm-select-wrap">
                    <Select
                      isMulti isClearable isSearchable
                      styles={selectStyles}
                      options={medicineTypeOptions.map((m) => ({ label: m, value: m }))}
                      value={medicineType}
                      onChange={(selected) => this.setState({ medicineType: selected || [] })}
                      placeholder="All Types"
                    />
                  </div>
                </div>
                <div className="adm-filter-group grow">
                  <label>Service For</label>
                  <div className="adm-select-wrap">
                    <Select
                      isMulti isClearable isSearchable
                      styles={selectStyles}
                      options={serviceForOptions.map((s) => ({ label: s, value: s }))}
                      value={serviceFor}
                      onChange={(selected) => this.setState({ serviceFor: selected || [] })}
                      placeholder="All Service For"
                    />
                  </div>
                </div>
                <div className="adm-filter-group grow">
                  <label>Location</label>
                  <div className="adm-select-wrap">
                    <AsyncSelect
                      isMulti cacheOptions defaultOptions
                      styles={selectStyles}
                      loadOptions={this.loadLocations}
                      value={location}
                      onChange={(selected) => this.setState({ location: selected || [] })}
                      placeholder="Search location..."
                      noOptionsMessage={() => "Type to search..."}
                    />
                  </div>
                </div>
                <div className="adm-filter-group">
                  <label style={{ visibility: "hidden" }}>Go</label>
                  <button className="adm-btn adm-btn-primary" onClick={this.fetchData} disabled={loading}>
                    {loading ? (
                      <><i className="fas fa-spinner fa-spin"></i>Loading...</>
                    ) : (
                      <><i className="fas fa-play"></i>Generate</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {dataFetched && (
            platforms.length === 0 ? (
              <div className="adm-card">
                <div className="adm-empty">
                  <i className="fas fa-inbox"></i>
                  <strong>No data for this selection</strong>
                  <span>No budget or spend recorded for the selected filters and date range.</span>
                </div>
              </div>
            ) : (
              <div className="adm-card" style={{ padding: 0, overflow: "hidden" }}>
                {platforms.map((p, idx) => {
                  const isExpanded = expandedPlatformId === p.id;
                  const totals = this.platformTotals(p.id);
                  const sourceMap = this.sourceBucketMap(p.id);
                  const platformSourceIds = Object.keys(sourceMap);

                  return (
                    <div key={p.id} style={{ borderBottom: idx < platforms.length - 1 ? "1px solid var(--adm-border)" : "none" }}>
                      <div
                        className={`adm-group-row${isExpanded ? " expanded" : ""}`}
                        onClick={() => this.togglePlatform(p.id)}
                      >
                        <div className="adm-group-title">
                          <i className={`fas fa-chevron-${isExpanded ? "down" : "right"}`} style={{ fontSize: "11px", color: "var(--adm-text-muted)" }}></i>
                          {p.name}
                        </div>
                        <div className="adm-group-metrics">
                          <span className="adm-cell-budget">₹ {totals.budget.toFixed(2)}<span className="m-lbl">Budget</span></span>
                          <span className="adm-cell-spent">₹ {totals.spent.toFixed(2)}<span className="m-lbl">Spent</span></span>
                          <span className="m-val">{totals.total_leads}<span className="m-lbl">Leads</span></span>
                          <span className="m-val">{totals.posted_leads}<span className="m-lbl">Posted</span></span>
                        </div>
                      </div>

                      {isExpanded && (
                        <div style={{ padding: "0 20px 20px" }}>
                          <div className="adm-matrix-wrap" style={{ maxHeight: "420px" }}>
                            <table className="adm-matrix-table">
                              <thead>
                                <tr>
                                  <th className="adm-matrix-pin">Source</th>
                                  {buckets.map((b) => (
                                    <th key={b} style={{ minWidth: "100px" }}>{formatBucketLabel(b, reportType)}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {platformSourceIds.map((sourceId) => (
                                  <tr key={sourceId}>
                                    <td className="adm-matrix-pin">{sourceById[sourceId] || `#${sourceId}`}</td>
                                    {buckets.map((b) => {
                                      const cell = sourceMap[sourceId][b];
                                      if (!cell) return <td key={b}><span className="adm-cell-muted">–</span></td>;
                                      return (
                                        <td key={b}>
                                          <div className="adm-cell-budget">₹{Number(cell.budget).toFixed(0)}</div>
                                          <div className="adm-cell-spent">₹{Number(cell.spent).toFixed(0)}</div>
                                          {Number(cell.total_leads) > 0 && (
                                            <div style={{ fontSize: "10px", color: "var(--adm-text-muted)" }}>{cell.total_leads}L / {cell.posted_leads}P</div>
                                          )}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <small className="text-muted d-block mt-2">
                            Each cell: Budget / Spent, and (when present) Total leads / Posted leads under the selected filters.
                          </small>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>
      </div>
    );
  }
}

export default AdminBudgetSummaryReportPage;
